import { AvailableTimesUnit } from "types/available-times";
import { TimeSlot } from "types/time-slot";
import { WebRule } from "types/web-rule";
import { WebRuleType } from "types/web-rule-type";

interface CategorizedWebRules {
    ordered: Array<{ type: WebRuleType; webRule: WebRule }>;
    interactive: WebRule[];
    commentOnly: WebRule[];
}

/**
 * Categorizes WebRules into interactive (has time slots) and comment-only (is Text Row)
 * Returns an ordered sequence preserving the original order while also providing categorized arrays
 *
 * @param webRules - Array of WebRules to categorize
 * @returns Object containing ordered sequence and categorized arrays
 */
function categorizeWebRules(webRules: WebRule[]): CategorizedWebRules {
    const openWebRules = webRules.filter((rule) => !rule.isClosed);

    const categorized = openWebRules.reduce(
        (acc, webRule) => {
            const isCommentOnly = webRule.isTextRow;
            const type = isCommentOnly ? WebRuleType.CommentOnly : WebRuleType.Interactive;

            acc.ordered.push({ type, webRule });

            if (isCommentOnly) {
                acc.commentOnly.push(webRule);
            } else {
                acc.interactive.push(webRule);
            }

            return acc;
        },
        {
            ordered: [] as Array<{ type: WebRuleType; webRule: WebRule }>,
            interactive: [] as WebRule[],
            commentOnly: [] as WebRule[],
        }
    );

    return categorized;
}

function mergeWebRulesByTitle(timeSets: WebRule[]): WebRule[] {
    const timeSetMap: { [key: string]: WebRule } = {};

    timeSets.forEach((timeSet) => {
        const key = timeSet.title;
        const existingTimeSet = timeSetMap[key];
        if (existingTimeSet) {
            const groupedTimes: { [key: string]: TimeSlot } = {};

            [...existingTimeSet.times, ...timeSet.times].forEach((time) => {
                const timeKey = time.start.format("YYYY-MM-DD HH:mm:ss");
                const existingTime = groupedTimes[timeKey];

                if (existingTime) {
                    // Merge availableSeats arrays, removing duplicates
                    const mergedAvailableSeats = [
                        ...new Set([...existingTime.availableSeats, ...time.availableSeats]),
                    ].sort((a, b) => a - b);

                    // Create merged time slot with combined availableSeats
                    groupedTimes[timeKey] = {
                        ...existingTime,
                        availableSeats: mergedAvailableSeats,
                    };
                } else {
                    groupedTimes[timeKey] = time;
                }
            });
            timeSetMap[key].times = Object.values(groupedTimes);
        } else {
            // Add the current timeSet directly to the map if it's not already there
            timeSetMap[key] = timeSet;
        }
    });

    return Object.values(timeSetMap);
}

const getRuleById = (ruleId: number, unit: AvailableTimesUnit[]): WebRule | null | undefined => {
    if (!unit) return null;
    return unit
        .flatMap((u) => u.sections)
        .flatMap((s) => s.timeSets)
        .find((t) => t.id === ruleId);
};

export { categorizeWebRules, mergeWebRulesByTitle, getRuleById };
